import { Table, Button, ScrollArea, Text } from "@mantine/core"
import { useEffect, useMemo, useRef, useState } from "react"
import { t } from "i18next"
import { notifications } from "@mantine/notifications"
import { useSuspenseQueryGet } from "../../../../../typed-client/hooks/use-suspense-query-get"
import { usePostMutation } from "../../../../../typed-client/hooks/use-post-mutation"
import { getFormattedDate } from "@/utils/get-formatted-date"
import type { DateOnly } from "@/@client/System"
import type { TheasoftActivity } from "@/@client/Theasoft_ApiServer_ProductionRoyalties"
import { LinkMappingModal } from "./link-mapping-modal"
import { MappedEventsModal } from "./mapped-events-modal"

interface Props {
  start: DateOnly
  end: DateOnly
}

export function ActivityMappingTable({ start, end }: Props) {
  const [linkModalOpened, setLinkModalOpened] = useState(false)
  const [linkedModalOpened, setLinkedModalOpened] = useState(false)
  const [selectedActivity, setSelectedActivity] =
    useState<TheasoftActivity | null>(null)
  const [selectedEvent, setSelectedEvent] = useState<TixlyEvent | null>(null)
  const period = {
    start,
    end,
    caption: "",
    totalDays: 0,
    isEmpty: false,
    isBaseOrEmpty: false,
  } as const

  const { data, refetch } = useSuspenseQueryGet(
    "/api/ActivitySale/GetSimpleActicityMappingList",
    period
  )
  const rows = Array.isArray(data) ? data : []
  const { mutateAsync: updateExternalIds } = usePostMutation(
    "/api/ActivitySale/UpdateExternalIds"
  )
  const { mutateAsync: updateSales } = usePostMutation(
    "/api/ActivitySale/UpdateSals"
  )
  const lastAutoPersistKeyRef = useRef<string>("")
  const autoPersistInFlightRef = useRef<string | null>(null)

  type MappingRow = (typeof rows)[number]
  type TixlyEvent = NonNullable<MappingRow["tixlyActivitiesOnDate"]>[number]

  const mappedEventIds = new Set(
    rows
      .map((row) => row.tixlyActivity?.eventId)
      .filter((eventId): eventId is number => eventId != null)
  )

  const tixlyEvents: TixlyEvent[] = Array.from(
    rows
      .flatMap((row) => row.tixlyActivitiesOnDate ?? [])
      .reduce((map, event) => {
        if (event.eventId == null) {
          return map
        }
        if (!map.has(event.eventId)) {
          map.set(event.eventId, event)
        }
        return map
      }, new Map<number, TixlyEvent>())
      .values()
  )

  const tixlyEventsWithoutMatch = tixlyEvents.filter(
    (event) => !mappedEventIds.has(event.eventId)
  )

  const autoLinkCandidates = useMemo(
    () =>
      rows
        .filter((row) => {
          if (!row.tixlyActivity) {
            return false
          }

          return (
            row.theasoftActivity.externalActivityId !==
            row.tixlyActivity.eventId
          )
        })
        .map((row) => ({
          activityId: row.theasoftActivity.activityId,
          eventId: row.tixlyActivity!.eventId,
          date: row.theasoftActivity.date,
        })),
    [rows]
  )

  const autoLinkKey = useMemo(
    () =>
      autoLinkCandidates
        .map((n) => `${n.activityId}:${n.eventId}`)
        .sort()
        .join("|"),
    [autoLinkCandidates]
  )

  useEffect(() => {
    if (!autoLinkKey) {
      return
    }

    if (
      autoLinkKey === lastAutoPersistKeyRef.current ||
      autoPersistInFlightRef.current === autoLinkKey
    ) {
      return
    }

    autoPersistInFlightRef.current = autoLinkKey

    const persistAutoLinks = async () => {
      try {
        await updateExternalIds(
          autoLinkCandidates.map((n) => ({
            activityId: n.activityId,
            externalActivityId: n.eventId,
          }))
        )

        const sortedDates = [
          ...new Set(autoLinkCandidates.map((n) => n.date)),
        ].sort()
        const startDate = sortedDates[0]
        const endDate = sortedDates[sortedDates.length - 1]

        await updateSales({
          start: startDate,
          end: endDate,
          caption: "",
          totalDays: 0,
          isEmpty: false,
          isBaseOrEmpty: false,
        })

        lastAutoPersistKeyRef.current = autoLinkKey
        await refetch()
      } catch (error) {
        notifications.show({
          title: t("AutoLinkFailed"),
          message:
            error instanceof Error ? error.message : t("AutoLinkFailedMessage"),
          color: "red",
        })
      } finally {
        autoPersistInFlightRef.current = null
      }
    }

    void persistAutoLinks()
  }, [autoLinkCandidates, autoLinkKey, refetch, updateExternalIds, updateSales])

  const unmatchedActivities = rows
    .filter((row) => !row.tixlyActivity)
    .map((row) => row.theasoftActivity)

  const getExternalLocationIds = (activity: TheasoftActivity): number[] => {
    const extended = activity as TheasoftActivity & {
      externalLocationIds?: number[]
    }
    if (
      extended.externalLocationIds &&
      extended.externalLocationIds.length > 0
    ) {
      return extended.externalLocationIds
    }

    return [activity.locationId]
  }

  const hasLocationMatch = (activity: TheasoftActivity, event: TixlyEvent) =>
    getExternalLocationIds(activity).includes(event.hallId)

  const isSameTime = (timeA: string, timeB: string) => {
    const [hA = "", mA = ""] = timeA.split(":")
    const [hB = "", mB = ""] = timeB.split(":")
    return hA === hB && mA === mB
  }

  const getActivityStatus = (
    activity: TheasoftActivity,
    linkedEvent: TixlyEvent | null
  ) => {
    if (linkedEvent) {
      return { color: "green", label: t("Linked"), canLink: false }
    }

    const locationMatchesSameDate = tixlyEventsWithoutMatch.filter(
      (event) =>
        event.date === activity.date && hasLocationMatch(activity, event)
    )

    const hasPossibleOnlyTimeIssue = locationMatchesSameDate.some(
      (event) => !isSameTime(event.startTime, activity.startTime)
    )

    if (hasPossibleOnlyTimeIssue) {
      return { color: "blue", label: t("Link"), canLink: true }
    }

    return { color: "red", label: t("Link"), canLink: true }
  }

  const getTixlyStatus = (
    event: TixlyEvent,
    linkedActivity: TheasoftActivity | null
  ) => {
    if (linkedActivity) {
      return { color: "green", label: t("Linked"), canLink: false }
    }

    const locationMatchesSameDate = unmatchedActivities.filter(
      (activity) =>
        activity.date === event.date && hasLocationMatch(activity, event)
    )

    const hasPossibleOnlyTimeIssue = locationMatchesSameDate.some(
      (activity) => !isSameTime(activity.startTime, event.startTime)
    )

    if (hasPossibleOnlyTimeIssue) {
      return { color: "blue", label: t("Link"), canLink: true }
    }

    return { color: "red", label: t("Link"), canLink: true }
  }

  const combinedRows = [
    ...rows.map((row) => ({
      theasoftActivity: row.theasoftActivity,
      tixlyEvent: row.tixlyActivity ?? null,
    })),
    ...tixlyEventsWithoutMatch.map((event) => ({
      theasoftActivity: null,
      tixlyEvent: event,
    })),
  ].sort((a, b) => {
    const aDate = a.theasoftActivity?.date ?? a.tixlyEvent?.date ?? ""
    const bDate = b.theasoftActivity?.date ?? b.tixlyEvent?.date ?? ""

    if (aDate !== bDate) {
      return aDate.localeCompare(bDate)
    }

    const aTime = a.theasoftActivity?.startTime ?? a.tixlyEvent?.startTime ?? ""
    const bTime = b.theasoftActivity?.startTime ?? b.tixlyEvent?.startTime ?? ""

    if (aTime !== bTime) {
      return aTime.localeCompare(bTime)
    }

    const aId = a.theasoftActivity?.activityId ?? a.tixlyEvent?.eventId ?? 0
    const bId = b.theasoftActivity?.activityId ?? b.tixlyEvent?.eventId ?? 0
    return aId - bId
  })

  if (combinedRows.length === 0) {
    return (
      <Text c="dimmed" ta="center" mt="xl">
        {t("NoActivitiesOrEventsInSelectedMonth")}
      </Text>
    )
  }

  const openLinkModal = (
    activity: TheasoftActivity | null,
    event: TixlyEvent | null
  ) => {
    setSelectedActivity(activity)
    setSelectedEvent(event)
    setLinkModalOpened(true)
  }

  const openLinkedModal = (activity: TheasoftActivity, event: TixlyEvent) => {
    setSelectedActivity(activity)
    setSelectedEvent(event)
    setLinkedModalOpened(true)
  }

  const formatEventTime = (value?: string | null) => {
    if (!value) return ""
    const [hour = "", minute = ""] = value.split(":")
    return `${hour}:${minute}`
  }

  return (
    <>
      <ScrollArea h="100%">
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th colSpan={5}>{t("DatabaseTheasoft")}</Table.Th>
              <Table.Th colSpan={5}>{t("Tixly")}</Table.Th>
            </Table.Tr>
            <Table.Tr>
              <Table.Th>{t("Date")}</Table.Th>
              <Table.Th>{t("Time")}</Table.Th>
              <Table.Th>{t("Title")}</Table.Th>
              <Table.Th>{t("Location")}</Table.Th>
              <Table.Th>{t("Status")}</Table.Th>
              <Table.Th>{t("Date")}</Table.Th>
              <Table.Th>{t("Time")}</Table.Th>
              <Table.Th>{t("Title")}</Table.Th>
              <Table.Th>{t("HallName")}</Table.Th>
              <Table.Th>{t("Status")}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {combinedRows.map((row, idx) => (
              <Table.Tr
                key={`${row.theasoftActivity?.activityId ?? "no-db"}-${row.tixlyEvent?.eventId ?? "no-tixly"}-${idx}`}
              >
                <Table.Td>
                  {row.theasoftActivity?.date
                    ? getFormattedDate(row.theasoftActivity.date)
                    : ""}
                </Table.Td>
                <Table.Td>
                  {formatEventTime(row.theasoftActivity?.startTime)}
                </Table.Td>
                <Table.Td>{row.theasoftActivity?.title ?? ""}</Table.Td>
                <Table.Td>{row.theasoftActivity?.location ?? ""}</Table.Td>
                <Table.Td>
                  {row.theasoftActivity
                    ? (() => {
                        const status = getActivityStatus(
                          row.theasoftActivity,
                          row.tixlyEvent
                        )
                        return (
                          <Button
                            variant={status.canLink ? "light" : "filled"}
                            color={status.color}
                            size="compact-sm"
                            onClick={() =>
                              status.canLink
                                ? openLinkModal(row.theasoftActivity, null)
                                : openLinkedModal(
                                    row.theasoftActivity,
                                    row.tixlyEvent
                                  )
                            }
                          >
                            {status.label}
                          </Button>
                        )
                      })()
                    : ""}
                </Table.Td>
                <Table.Td>
                  {row.tixlyEvent?.date
                    ? getFormattedDate(row.tixlyEvent.date)
                    : ""}
                </Table.Td>
                <Table.Td>
                  {formatEventTime(row.tixlyEvent?.startTime)}
                </Table.Td>
                <Table.Td>{row.tixlyEvent?.title ?? ""}</Table.Td>
                <Table.Td>{row.tixlyEvent?.hallName ?? ""}</Table.Td>
                <Table.Td>
                  {row.tixlyEvent
                    ? (() => {
                        const status = getTixlyStatus(
                          row.tixlyEvent,
                          row.theasoftActivity
                        )
                        return (
                          <Button
                            variant={status.canLink ? "light" : "filled"}
                            color={status.color}
                            size="compact-sm"
                            onClick={() => {
                              if (status.canLink) {
                                openLinkModal(null, row.tixlyEvent)
                                return
                              }

                              if (row.theasoftActivity) {
                                openLinkedModal(
                                  row.theasoftActivity,
                                  row.tixlyEvent
                                )
                              }
                            }}
                          >
                            {status.label}
                          </Button>
                        )
                      })()
                    : ""}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </ScrollArea>

      <LinkMappingModal
        opened={linkModalOpened}
        onClose={() => {
          setLinkModalOpened(false)
          setSelectedActivity(null)
          setSelectedEvent(null)
        }}
        activity={selectedActivity}
        tixlyEvent={selectedEvent}
        allActivitiesOnDate={
          selectedActivity || selectedEvent
            ? (rows
                .filter(
                  (row) =>
                    row.theasoftActivity?.date ===
                    (selectedActivity?.date ?? selectedEvent?.date)
                )
                .map((row) => row.theasoftActivity)
                .filter((a) => a !== null) as TheasoftActivity[])
            : []
        }
        allEventsOnDate={
          selectedActivity || selectedEvent
            ? tixlyEvents.filter(
                (e) =>
                  e.date === (selectedActivity?.date ?? selectedEvent?.date)
              )
            : []
        }
        mappedEventIds={mappedEventIds}
        onSuccess={() => {
          refetch()
        }}
      />

      {selectedActivity && selectedEvent && (
        <MappedEventsModal
          opened={linkedModalOpened}
          onClose={() => {
            setLinkedModalOpened(false)
            setSelectedActivity(null)
            setSelectedEvent(null)
          }}
          onSuccess={() => {
            refetch()
          }}
          activity={selectedActivity}
          tixlyEvent={selectedEvent}
        />
      )}
    </>
  )
}
